import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Trophy, Users, Trash2, Plus, CalendarIcon, Clock, Pencil, ArrowLeft, HelpCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ImageUploader } from "@/components/ImageUploader";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

import guessTheNameImg from "@/assets/game-templates/guess_the_name.png";
import guessTheWeightImg from "@/assets/game-templates/guess_the_weight.png";
import numberOfJellyBeansImg from "@/assets/game-templates/number_of_jelly_beans.png";

interface GameTemplate {
  id: string;
  name: string;
  gameType: string;
  imageAsset: string;
  entryPrice: string;
  winnerSelection: string;
  description: string;
  rangeStart?: string;
  rangeEnd?: string;
}

const GAME_TEMPLATES: GameTemplate[] = [
  {
    id: "teddy-name",
    name: "Guess the Name of the Teddy",
    gameType: "name",
    imageAsset: guessTheNameImg,
    entryPrice: "1.00",
    winnerSelection: "automatic",
    description: "Players guess the teddy bear's secret name. A random guess is drawn as the winner.",
  },
  {
    id: "cake-weight",
    name: "Guess the Weight of the Cake",
    gameType: "weight",
    imageAsset: guessTheWeightImg,
    entryPrice: "1.00",
    winnerSelection: "automatic",
    description: "Players guess the cake's weight. A random guess is drawn as the winner.",
  },
  {
    id: "jelly-beans",
    name: "Guess the Number of Jelly Beans",
    gameType: "quantity",
    imageAsset: numberOfJellyBeansImg,
    entryPrice: "1.00",
    winnerSelection: "automatic",
    description: "Players guess how many jelly beans are in the jar. A random guess is drawn as the winner.",
  },
];

interface GuessingGameType {
  id: string;
  pta_id: string;
  name: string;
  game_type: string;
  image_url: string | null;
  entry_price: number;
  correct_answer: string | null;
  winner_selection: string;
  start_date: string | null;
  end_date: string | null;
  range_start: number | null;
  range_end: number | null;
  status: string;
  winner_id: string | null;
  winner_selection_date: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface GuessingGameEntry {
  id: string;
  game_id: string;
  pta_id: string;
  parent_id: string | null;
  player_name: string;
  guess_value: string | null;
  number_picked: number | null;
  created_at: string;
}

export default function GuessingGame() {
  const { user } = useAuth();
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [games, setGames] = useState<GuessingGameType[]>([]);
  const [entries, setEntries] = useState<Record<string, GuessingGameEntry[]>>({});
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState<'template' | 'form'>('template');
  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [usedTemplateId, setUsedTemplateId] = useState<string | null>(null);
  const [gameType, setGameType] = useState("weight");
  const [gameName, setGameName] = useState("");
  const [gameImages, setGameImages] = useState<string[]>([]);
  const [entryPrice, setEntryPrice] = useState("1.00");
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [winnerSelection, setWinnerSelection] = useState("automatic");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [startTime, setStartTime] = useState("09:00");
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [endTime, setEndTime] = useState("18:00");
  const [winnerDelayDate, setWinnerDelayDate] = useState<Date | undefined>();
  const [winnerDelayTime, setWinnerDelayTime] = useState("18:00");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [creating, setCreating] = useState(false);

  // Player state
  const [playDialogOpen, setPlayDialogOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<GuessingGameType | null>(null);
  const [playerName, setPlayerName] = useState("");
  const [guessValue, setGuessValue] = useState("");
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Fetch PTA context
  useEffect(() => {
    if (!user) return;
    const fetchContext = async () => {
      const { data: parentData } = await supabase
        .from("parents")
        .select("pta_id")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (parentData) {
        setPtaId(parentData.pta_id);
      } else {
        const { data: memberData } = await supabase
          .from("members")
          .select("pta_id, is_pta_admin")
          .eq("user_id", user.id)
          .maybeSingle();
        if (memberData) {
          setPtaId(memberData.pta_id);
          setIsAdmin(memberData.is_pta_admin || false);
        }
      }

      if (parentData) {
        const { data: adminCheck } = await supabase
          .from("members")
          .select("is_pta_admin")
          .eq("user_id", user.id)
          .eq("pta_id", parentData.pta_id)
          .maybeSingle();
        if (adminCheck?.is_pta_admin) setIsAdmin(true);
      }
    };
    fetchContext();
  }, [user]);

  const fetchGames = useCallback(async () => {
    if (!ptaId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("guessing_games")
      .select("*")
      .eq("pta_id", ptaId)
      .order("created_at", { ascending: false });
    
    if (error) {
      console.error("Error fetching games:", error);
    } else {
      setGames((data as GuessingGameType[]) || []);
    }
    setLoading(false);
  }, [ptaId]);

  useEffect(() => { fetchGames(); }, [fetchGames]);

  useEffect(() => {
    if (!ptaId || games.length === 0) return;
    const fetchAllEntries = async () => {
      const gameIds = games.map(g => g.id);
      const { data } = await supabase
        .from("guessing_game_entries")
        .select("*")
        .in("game_id", gameIds);
      
      if (data) {
        const grouped: Record<string, GuessingGameEntry[]> = {};
        (data as GuessingGameEntry[]).forEach(e => {
          if (!grouped[e.game_id]) grouped[e.game_id] = [];
          grouped[e.game_id].push(e);
        });
        setEntries(grouped);
      }
    };
    fetchAllEntries();
  }, [ptaId, games]);

  useEffect(() => {
    if (!ptaId) return;
    const channel = supabase
      .channel("guessing-entries")
      .on("postgres_changes", { event: "*", schema: "public", table: "guessing_game_entries", filter: `pta_id=eq.${ptaId}` }, () => {
        fetchGames();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [ptaId, fetchGames]);

  // Auto-complete games past end_date / winner_selection_date
  useEffect(() => {
    const checkAutoComplete = async () => {
      const now = new Date();
      for (const game of games) {
        if (game.status !== "active") continue;
        
        const gameEntries = entries[game.id] || [];
        if (gameEntries.length === 0) continue;

        if (game.winner_selection === "automatic") {
          // For automatic: check winner_selection_date (or end_date as fallback)
          const selectionDate = game.winner_selection_date 
            ? new Date(game.winner_selection_date) 
            : game.end_date ? new Date(game.end_date) : null;
          
          if (selectionDate && now >= selectionDate) {
            // Random draw from all entries
            const randomIndex = Math.floor(Math.random() * gameEntries.length);
            const winnerId = gameEntries[randomIndex].id;

            await supabase
              .from("guessing_games")
              .update({ status: "completed", winner_id: winnerId } as any)
              .eq("id", game.id);
            fetchGames();
          }
        } else if (game.winner_selection === "manual" && game.correct_answer) {
          // For manual with correct answer: closest guess logic after end date
          if (game.end_date && now >= new Date(game.end_date)) {
            const correctNum = parseFloat(game.correct_answer);
            let winnerId: string | null = null;

            if (!isNaN(correctNum)) {
              let closestDiff = Infinity;
              for (const entry of gameEntries) {
                const val = entry.number_picked != null ? entry.number_picked : parseFloat(entry.guess_value || "");
                if (!isNaN(val)) {
                  const diff = Math.abs(val - correctNum);
                  if (diff < closestDiff) {
                    closestDiff = diff;
                    winnerId = entry.id;
                  }
                }
              }
            } else {
              // For text-based (name): exact match or first entry
              const exactMatch = gameEntries.find(e => 
                (e.guess_value || "").toLowerCase().trim() === game.correct_answer!.toLowerCase().trim()
              );
              winnerId = exactMatch ? exactMatch.id : null;
            }

            if (winnerId) {
              await supabase
                .from("guessing_games")
                .update({ status: "completed", winner_id: winnerId } as any)
                .eq("id", game.id);
              fetchGames();
            }
          }
        }
      }
    };

    if (games.length > 0) {
      checkAutoComplete();
      const interval = setInterval(checkAutoComplete, 30000);
      return () => clearInterval(interval);
    }
  }, [games, entries, fetchGames]);

  // Helper: is game currently active (past start_date, before end_date)
  const isGameLive = (game: GuessingGameType) => {
    const now = new Date();
    if (game.start_date && now < new Date(game.start_date)) return false;
    return true;
  };

  const isGameEnded = (game: GuessingGameType) => {
    return game.end_date && new Date(game.end_date) <= new Date();
  };

  const canEditGame = (game: GuessingGameType) => {
    return !isGameLive(game);
  };

  const resetWizard = () => {
    setEditingGameId(null);
    setUsedTemplateId(null);
    setWizardStep('template');
    setGameType("weight");
    setGameName("");
    setGameImages([]);
    setEntryPrice("1.00");
    setCorrectAnswer("");
    setWinnerSelection("automatic");
    setStartDate(undefined);
    setStartTime("09:00");
    setEndDate(undefined);
    setEndTime("18:00");
    setWinnerDelayDate(undefined);
    setWinnerDelayTime("18:00");
    setRangeStart("");
    setRangeEnd("");
  };

  const openEditWizard = (game: GuessingGameType) => {
    setEditingGameId(game.id);
    setUsedTemplateId(null);
    setWizardStep('form');
    setGameType(game.game_type);
    setGameName(game.name);
    setGameImages(game.image_url ? [game.image_url] : []);
    setEntryPrice(String(game.entry_price));
    setCorrectAnswer(game.correct_answer || "");
    setWinnerSelection(game.winner_selection);
    if (game.start_date) {
      const sd = new Date(game.start_date);
      setStartDate(sd);
      setStartTime(format(sd, "HH:mm"));
    } else {
      setStartDate(undefined);
      setStartTime("09:00");
    }
    if (game.end_date) {
      const ed = new Date(game.end_date);
      setEndDate(ed);
      setEndTime(format(ed, "HH:mm"));
    } else {
      setEndDate(undefined);
      setEndTime("18:00");
    }
    if (game.winner_selection_date) {
      const wd = new Date(game.winner_selection_date);
      setWinnerDelayDate(wd);
      setWinnerDelayTime(format(wd, "HH:mm"));
    } else {
      setWinnerDelayDate(undefined);
      setWinnerDelayTime("18:00");
    }
    setRangeStart(game.range_start != null ? String(game.range_start) : "");
    setRangeEnd(game.range_end != null ? String(game.range_end) : "");
    setWizardOpen(true);
  };

  const applyTemplate = (template: GameTemplate) => {
    setUsedTemplateId(template.id);
    setGameType(template.gameType);
    setGameName(template.name);
    setGameImages([template.imageAsset]);
    setEntryPrice(template.entryPrice);
    setWinnerSelection(template.winnerSelection);
    setCorrectAnswer("");
    setRangeStart(template.rangeStart || "");
    setRangeEnd(template.rangeEnd || "");
    setStartDate(undefined);
    setStartTime("09:00");
    setEndDate(undefined);
    setEndTime("18:00");
    setWinnerDelayDate(undefined);
    setWinnerDelayTime("18:00");
    setWizardStep('form');
  };

  const buildDatetime = (date: Date | undefined, time: string): string | null => {
    if (!date) return null;
    const [hours, minutes] = time.split(":").map(Number);
    const d = new Date(date);
    d.setHours(hours, minutes, 0, 0);
    return d.toISOString();
  };

  const handleSaveGame = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !ptaId) return;

    if (!gameName.trim()) {
      toast({ title: "Game name required", variant: "destructive" });
      return;
    }

    if (!endDate) {
      toast({ title: "End date required", description: "Please specify when the game should end.", variant: "destructive" });
      return;
    }

    if (!endTime) {
      toast({ title: "End time required", description: "Please specify the time the game should end.", variant: "destructive" });
      return;
    }

    // Auto-inject correct answer into range for manual + number type
    let finalRangeStart = gameType === "number" ? parseInt(rangeStart) : null;
    let finalRangeEnd = gameType === "number" ? parseInt(rangeEnd) : null;

    if (gameType === "number") {
      if (finalRangeStart === null || finalRangeEnd === null || isNaN(finalRangeStart) || isNaN(finalRangeEnd) || finalRangeStart >= finalRangeEnd) {
        toast({ title: "Invalid range", description: "Range start must be less than range end", variant: "destructive" });
        return;
      }

      if (winnerSelection === "manual" && correctAnswer.trim()) {
        const ca = parseInt(correctAnswer);
        if (!isNaN(ca)) {
          // Auto-extend range to include correct answer
          if (ca < finalRangeStart) finalRangeStart = ca;
          if (ca > finalRangeEnd) finalRangeEnd = ca;
        }
      }
    }

    const startDateFull = buildDatetime(startDate, startTime);
    const endDateFull = buildDatetime(endDate, endTime);
    const winnerSelectionDateFull = winnerSelection === "automatic" 
      ? buildDatetime(winnerDelayDate, winnerDelayTime) 
      : null;

    // If template image (local asset), upload to storage first
    let finalImageUrl = gameImages[0] || null;
    if (finalImageUrl && !finalImageUrl.startsWith("http")) {
      try {
        const response = await fetch(finalImageUrl);
        const blob = await response.blob();
        const ext = blob.type.split("/")[1] === "jpeg" ? "jpg" : (blob.type.split("/")[1] || "png");
        const fileName = `${ptaId}/guessing-game/${Date.now()}-template.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("shop-images")
          .upload(fileName, blob, { contentType: blob.type, upsert: true });
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from("shop-images").getPublicUrl(fileName);
          finalImageUrl = urlData.publicUrl;
        }
      } catch (err) {
        console.error("Failed to upload template image:", err);
      }
    }

    const payload: any = {
      name: gameName.trim(),
      game_type: gameType,
      image_url: finalImageUrl,
      entry_price: parseFloat(entryPrice) || 1,
      correct_answer: winnerSelection === "manual" ? (correctAnswer.trim() || null) : null,
      winner_selection: winnerSelection,
      start_date: startDateFull,
      end_date: endDateFull,
      winner_selection_date: winnerSelectionDateFull,
      range_start: finalRangeStart,
      range_end: finalRangeEnd,
    };

    setCreating(true);

    if (editingGameId) {
      const { error } = await supabase
        .from("guessing_games")
        .update(payload)
        .eq("id", editingGameId);
      setCreating(false);
      if (error) {
        toast({ title: "Error updating game", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Game updated!" });
        setWizardOpen(false);
        resetWizard();
        fetchGames();
      }
    } else {
      payload.pta_id = ptaId;
      payload.created_by = user.id;
      const { error } = await supabase.from("guessing_games").insert(payload);
      setCreating(false);
      if (error) {
        toast({ title: "Error creating game", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Game created!" });
        setWizardOpen(false);
        resetWizard();
        fetchGames();
      }
    }
  };

  const handleDeleteGame = async (gameId: string) => {
    const { error } = await supabase.from("guessing_games").delete().eq("id", gameId);
    if (error) {
      toast({ title: "Error deleting game", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Game deleted" });
      fetchGames();
    }
  };

  const handlePickWinner = async (game: GuessingGameType) => {
    const gameEntries = entries[game.id] || [];
    if (gameEntries.length === 0) {
      toast({ title: "No entries", description: "No one has entered this game yet", variant: "destructive" });
      return;
    }
    if (!game.correct_answer) {
      toast({ title: "No correct answer set", variant: "destructive" });
      return;
    }

    const correctNum = parseFloat(game.correct_answer);
    let winnerId: string | null = null;

    if (!isNaN(correctNum)) {
      let closestDiff = Infinity;
      for (const entry of gameEntries) {
        const val = entry.number_picked != null ? entry.number_picked : parseFloat(entry.guess_value || "");
        if (!isNaN(val)) {
          const diff = Math.abs(val - correctNum);
          if (diff < closestDiff) {
            closestDiff = diff;
            winnerId = entry.id;
          }
        }
      }
    } else {
      // Text-based: exact match
      const exactMatch = gameEntries.find(e =>
        (e.guess_value || "").toLowerCase().trim() === game.correct_answer!.toLowerCase().trim()
      );
      winnerId = exactMatch ? exactMatch.id : null;
    }

    // Fallback: if no match found, random pick
    if (!winnerId) {
      const randomIndex = Math.floor(Math.random() * gameEntries.length);
      winnerId = gameEntries[randomIndex].id;
    }

    const { error } = await supabase
      .from("guessing_games")
      .update({ status: "completed", winner_id: winnerId } as any)
      .eq("id", game.id);
    if (error) {
      toast({ title: "Error picking winner", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Winner selected!" });
      fetchGames();
    }
  };

  const handleSubmitEntry = async () => {
    if (!selectedGame || !user || !ptaId) return;
    if (!playerName.trim()) {
      toast({ title: "Enter your name", variant: "destructive" });
      return;
    }

    setSubmitting(true);

    if (selectedGame.game_type === "number") {
      if (selectedNumbers.length === 0) {
        toast({ title: "Select at least one number", variant: "destructive" });
        setSubmitting(false);
        return;
      }
      const entriesToInsert = selectedNumbers.map(n => ({
        game_id: selectedGame.id,
        pta_id: ptaId,
        parent_id: user.id,
        player_name: playerName.trim(),
        number_picked: n,
        guess_value: String(n),
      }));
      const { error } = await supabase.from("guessing_game_entries").insert(entriesToInsert as any);
      if (error) {
        toast({ title: "Error submitting", description: error.message, variant: "destructive" });
      } else {
        toast({ title: `${selectedNumbers.length} number(s) submitted!` });
        setPlayDialogOpen(false);
        setSelectedNumbers([]);
        setPlayerName("");
        fetchGames();
      }
    } else {
      if (!guessValue.trim()) {
        toast({ title: "Enter your guess", variant: "destructive" });
        setSubmitting(false);
        return;
      }
      const { error } = await supabase.from("guessing_game_entries").insert({
        game_id: selectedGame.id,
        pta_id: ptaId,
        parent_id: user.id,
        player_name: playerName.trim(),
        guess_value: guessValue.trim(),
      } as any);
      if (error) {
        toast({ title: "Error submitting", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Guess submitted!" });
        setPlayDialogOpen(false);
        setGuessValue("");
        setPlayerName("");
        fetchGames();
      }
    }
    setSubmitting(false);
  };

  const activeGames = games.filter(g => g.status === "active");
  const completedGames = games.filter(g => g.status === "completed");

  const getWinnerEntry = (game: GuessingGameType) => {
    if (!game.winner_id) return null;
    const gameEntries = entries[game.id] || [];
    return gameEntries.find(e => e.id === game.winner_id) || null;
  };

  const getPickedNumbers = (gameId: string): Set<number> => {
    const gameEntries = entries[gameId] || [];
    return new Set(gameEntries.filter(e => e.number_picked != null).map(e => e.number_picked!));
  };

  const getGameStatus = (game: GuessingGameType) => {
    const now = new Date();
    if (game.start_date && now < new Date(game.start_date)) return "scheduled";
    if (game.end_date && now >= new Date(game.end_date)) return "ended";
    return "live";
  };

  const getCorrectAnswerConfig = () => {
    switch (gameType) {
      case "weight":
        return { type: "number", placeholder: "e.g., 2.5", suffix: "kg" };
      case "name":
        return { type: "text", placeholder: "e.g., Barnaby", suffix: null };
      case "quantity":
        return { type: "number", placeholder: "e.g., 342", suffix: "qty" };
      case "number":
        return { type: "number", placeholder: "e.g., 42", suffix: null };
      default:
        return { type: "text", placeholder: "Enter correct answer", suffix: null };
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <p className="text-muted-foreground">Please log in to access games.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl space-y-6 animate-fade-in">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Guessing Game</h1>
          <p className="text-muted-foreground mt-2">
            Create interactive guessing games for your events
          </p>
          <Badge className="mt-2 bg-primary">Standard Plan Feature</Badge>
        </div>
        {isAdmin && (
          <Button onClick={() => { resetWizard(); setWizardOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Game
          </Button>
        )}
      </div>

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active">Active Games ({activeGames.length})</TabsTrigger>
          <TabsTrigger value="completed">Completed Games ({completedGames.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : activeGames.length === 0 ? (
            <Card className="shadow-card">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No active games. {isAdmin ? "Create one to get started!" : ""}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeGames.map(game => {
                const gameEntries = entries[game.id] || [];
                const status = getGameStatus(game);
                const live = status === "live";
                const scheduled = status === "scheduled";
                const ended = status === "ended";

                return (
                  <Card key={game.id} className="shadow-card">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <CardTitle className="text-sm font-semibold truncate">{game.name}</CardTitle>
                          <CardDescription className="capitalize text-xs">{game.game_type} game</CardDescription>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {isAdmin && canEditGame(game) && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditWizard(game)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {isAdmin && !live && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-foreground hover:text-destructive"
                              onClick={() => handleDeleteGame(game.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Badge variant={scheduled ? "outline" : ended ? "destructive" : "default"} className="text-xs">
                            {scheduled ? "Scheduled" : ended ? "Ended" : "Live"}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-0">
                      {game.image_url && (
                        <div className="h-40 rounded-lg overflow-hidden">
                          <img src={game.image_url} alt={game.name} className="w-full h-full object-contain" />
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 bg-muted/30 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">Entries</p>
                          <p className="text-lg font-bold text-primary">{gameEntries.length}</p>
                        </div>
                        <div className="p-2 bg-muted/30 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">Price</p>
                          <p className="text-lg font-bold">£{Number(game.entry_price).toFixed(2)}</p>
                        </div>
                      </div>

                      {game.start_date && scheduled && (
                        <div className="p-2 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Starts</p>
                          <p className="text-xs font-medium">{format(new Date(game.start_date), "PPP 'at' HH:mm")}</p>
                        </div>
                      )}

                      {game.end_date && (
                        <div className="p-2 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Ends</p>
                          <p className="text-xs font-medium">{format(new Date(game.end_date), "PPP 'at' HH:mm")}</p>
                        </div>
                      )}

                      {game.game_type === "number" && game.range_start != null && game.range_end != null && (
                        <div className="p-2 bg-muted/30 rounded-lg">
                          <p className="text-xs text-muted-foreground">Range: {game.range_start} – {game.range_end}</p>
                        </div>
                      )}

                      {gameEntries.length > 0 && (
                        <div className="pt-2 border-t border-border">
                          <h4 className="font-semibold mb-2 text-xs">Recent Entries (last 5)</h4>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {gameEntries.slice(0, 5).map(entry => (
                              <div key={entry.id} className="flex items-center justify-between p-1.5 bg-muted/30 rounded text-xs">
                                <p className="font-medium truncate">{entry.player_name}</p>
                                <span className="font-bold text-primary shrink-0 ml-2">
                                  {entry.number_picked != null ? `#${entry.number_picked}` : entry.guess_value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2">
                        {scheduled && (
                          <p className="text-xs text-muted-foreground italic w-full text-center py-1">
                            Game not yet started
                          </p>
                        )}
                        {isAdmin && game.winner_selection === "manual" && live && (
                          <Button variant="outline" size="sm" onClick={() => handlePickWinner(game)}>
                            <Trophy className="h-3.5 w-3.5 mr-1" />
                            Pick Winner
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed">
          {completedGames.length === 0 ? (
            <Card className="shadow-card">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No completed games yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {completedGames.map(game => {
                const winner = getWinnerEntry(game);
                const gameEntries = entries[game.id] || [];
                return (
                  <Card key={game.id} className="shadow-card">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{game.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            Completed: {format(new Date(game.updated_at || game.created_at), "PP")}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Users className="h-4 w-4 text-primary" />
                            <span className="text-sm">{gameEntries.length} entries</span>
                          </div>
                          {winner && (
                            <p className="text-sm mt-2 font-medium">
                              🏆 Winner: {winner.player_name} (
                              {winner.number_picked != null ? `#${winner.number_picked}` : winner.guess_value}
                              {game.correct_answer ? `, actual: ${game.correct_answer}` : ""})
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">Completed</Badge>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-foreground hover:text-destructive"
                              onClick={() => handleDeleteGame(game.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Game Wizard Dialog */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingGameId ? "Edit Guessing Game" : wizardStep === 'template' ? "Choose a Template" : "Create Guessing Game"}</DialogTitle>
            <DialogDescription>
              {wizardStep === 'template' ? "Pick a ready-made game or start from scratch" : "Set up your guessing game details"}
            </DialogDescription>
          </DialogHeader>

          {/* Template Selection Step */}
          {wizardStep === 'template' && !editingGameId && (
            <div className="grid grid-cols-2 gap-4">
              {GAME_TEMPLATES.map(template => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => applyTemplate(template)}
                  className="group rounded-lg border border-border bg-card p-3 text-left transition-all hover:border-primary hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <div className="h-28 rounded-md overflow-hidden mb-3">
                    <img src={template.imageAsset} alt={template.name} className="w-full h-full object-contain" />
                  </div>
                  <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">{template.name}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                </button>
              ))}
              <button
                type="button"
                onClick={() => { resetWizard(); setEditingGameId(null); setWizardStep('form'); setWizardOpen(true); }}
                className="group rounded-lg border-2 border-dashed border-border bg-card p-3 text-left transition-all hover:border-primary hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring flex flex-col items-center justify-center min-h-[200px]"
              >
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3 group-hover:bg-primary/10 transition-colors">
                  <Plus className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <h4 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">Custom Game</h4>
                <p className="text-xs text-muted-foreground mt-1">Start from scratch</p>
              </button>
            </div>
          )}

          {/* Form Step */}
          {wizardStep === 'form' && (
            <form onSubmit={handleSaveGame} className="space-y-4">
              {!editingGameId && (
                <Button type="button" variant="ghost" size="sm" onClick={() => { setUsedTemplateId(null); setWizardStep('template'); }} className="mb-2">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Templates
                </Button>
              )}

              {!usedTemplateId && (
                <div className="space-y-2">
                  <Label htmlFor="game-type">Game Type</Label>
                  <Select value={gameType} onValueChange={setGameType}>
                    <SelectTrigger id="game-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weight">Guess the Weight</SelectItem>
                      <SelectItem value="name">Guess the Name</SelectItem>
                      <SelectItem value="number">Guess the Number</SelectItem>
                      <SelectItem value="quantity">Guess the Quantity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="game-name">Game Name</Label>
                <Input
                  id="game-name"
                  placeholder="e.g., Guess the Weight of the Cake"
                  value={gameName}
                  onChange={e => setGameName(e.target.value)}
                  required
                />
              </div>

              {/* Image: show template preview only if template is used, otherwise show uploader */}
              <div className="space-y-2">
                <Label>Game Image</Label>
                {usedTemplateId ? (
                  <div className="h-32 rounded-lg overflow-hidden bg-muted border border-border">
                    <img src={gameImages[0]} alt="Template image" className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <ImageUploader
                    images={gameImages}
                    onChange={setGameImages}
                    maxImages={1}
                    ptaId={ptaId || "temp"}
                    itemId="guessing-game"
                    bucket="shop-images"
                    id="guessing-game-image"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="game-price">Entry Price (£)</Label>
                <Input
                  id="game-price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="1.00"
                  value={entryPrice}
                  onChange={e => setEntryPrice(e.target.value)}
                />
              </div>

              {gameType === "number" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="range-start">Range Start</Label>
                    <Input
                      id="range-start"
                      type="number"
                      placeholder="1"
                      value={rangeStart}
                      onChange={e => setRangeStart(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="range-end">Range End</Label>
                    <Input
                      id="range-end"
                      type="number"
                      placeholder="100"
                      value={rangeEnd}
                      onChange={e => setRangeEnd(e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Start Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        type="button"
                        className={cn("w-full justify-start text-left font-normal", !startDate && "text-muted-foreground")}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start-time">Start Time</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="start-time"
                      type="time"
                      value={startTime}
                      onChange={e => setStartTime(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* End Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        type="button"
                        className={cn("w-full justify-start text-left font-normal", !endDate && "text-muted-foreground")}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        disabled={date => date < new Date()}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-time">End Time</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="end-time"
                      type="time"
                      value={endTime}
                      onChange={e => setEndTime(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Winner Selection */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Winner Selection</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs" side="right" align="start">
                        <p><strong>Automatic:</strong> The system randomly picks a winner from all entries once the winner selection delay has passed.</p>
                        <p className="mt-1"><strong>Manual:</strong> You set the correct answer — if no one guesses it, the system falls back to a random pick.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="space-y-2">
                  <label className="flex items-start space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="selection"
                      checked={winnerSelection === "automatic"}
                      onChange={() => setWinnerSelection("automatic")}
                      className="mt-1"
                    />
                    <div>
                      <span className="text-sm font-medium">Automatic — Random draw</span>
                      <p className="text-xs text-muted-foreground">The system randomly picks a winner after the winner selection delay has passed</p>
                    </div>
                  </label>
                  <label className="flex items-start space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="selection"
                      checked={winnerSelection === "manual"}
                      onChange={() => setWinnerSelection("manual")}
                      className="mt-1"
                    />
                    <div>
                      <span className="text-sm font-medium">Manual — Correct answer</span>
                      <p className="text-xs text-muted-foreground">You set the correct answer, closest guess wins. If no one picks it, a random entry is chosen instead</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Winner Selection Delay — only for automatic */}
              {winnerSelection === "automatic" && (
                <div className="space-y-2 p-3 border border-border rounded-lg bg-muted/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label>Winner Selection Date & Time</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>How long after the game ends before the system picks a winner. Use "Same as end date" to pick immediately when the game closes.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {endDate && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setWinnerDelayDate(endDate);
                          setWinnerDelayTime(endTime);
                        }}
                      >
                        Same as end date
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          type="button"
                          className={cn("w-full justify-start text-left font-normal", !winnerDelayDate && "text-muted-foreground")}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {winnerDelayDate ? format(winnerDelayDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={winnerDelayDate}
                          onSelect={setWinnerDelayDate}
                          disabled={date => date < new Date()}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="time"
                        value={winnerDelayTime}
                        onChange={e => setWinnerDelayTime(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Correct Answer — only for manual */}
              {winnerSelection === "manual" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="game-answer">Correct Answer</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="game-answer"
                        type={getCorrectAnswerConfig().type}
                        step={getCorrectAnswerConfig().type === "number" ? "0.01" : undefined}
                        placeholder={getCorrectAnswerConfig().placeholder}
                        value={correctAnswer}
                        onChange={e => setCorrectAnswer(e.target.value)}
                      />
                      {getCorrectAnswerConfig().suffix && (
                        <span className="text-sm font-medium text-muted-foreground shrink-0">
                          {getCorrectAnswerConfig().suffix}
                        </span>
                      )}
                    </div>
                    {gameType === "number" && (
                      <p className="text-xs text-muted-foreground">
                        If outside the range, it will be extended automatically.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setWizardOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? "Saving..." : editingGameId ? "Save Changes" : "Create Game"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={playDialogOpen} onOpenChange={setPlayDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Play: {selectedGame?.name}</DialogTitle>
            <DialogDescription>
              Entry price: £{selectedGame ? Number(selectedGame.entry_price).toFixed(2) : "0.00"} per guess
            </DialogDescription>
          </DialogHeader>
          {selectedGame && (
            <div className="space-y-4">
              {selectedGame.image_url && (
                <div className="h-48 rounded-lg overflow-hidden bg-muted">
                  <img src={selectedGame.image_url} alt={selectedGame.name} className="w-full h-full object-contain" />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="player-name">Your Name</Label>
                <Input
                  id="player-name"
                  placeholder="Enter your name"
                  value={playerName}
                  onChange={e => setPlayerName(e.target.value)}
                />
              </div>

              {selectedGame.game_type === "number" && selectedGame.range_start != null && selectedGame.range_end != null ? (
                <div className="space-y-3">
                  <Label>Pick your numbers ({selectedGame.range_start} – {selectedGame.range_end})</Label>
                  <p className="text-xs text-muted-foreground">
                    Each number selected counts as one play (£{Number(selectedGame.entry_price).toFixed(2)} each). 
                    Grey numbers are already taken.
                  </p>
                  {selectedNumbers.length > 0 && (
                    <div className="p-2 bg-primary/10 rounded-lg text-sm">
                      Selected: {selectedNumbers.length} number(s) — Total: £{(selectedNumbers.length * Number(selectedGame.entry_price)).toFixed(2)}
                    </div>
                  )}
                  <div className="grid grid-cols-10 gap-1.5 max-h-64 overflow-y-auto p-2">
                    {Array.from(
                      { length: selectedGame.range_end - selectedGame.range_start + 1 },
                      (_, i) => selectedGame.range_start! + i
                    ).map(num => {
                      const picked = getPickedNumbers(selectedGame.id).has(num);
                      const selected = selectedNumbers.includes(num);
                      return (
                        <button
                          key={num}
                          type="button"
                          disabled={picked}
                          onClick={() => {
                            setSelectedNumbers(prev =>
                              selected ? prev.filter(n => n !== num) : [...prev, num]
                            );
                          }}
                          className={cn(
                            "aspect-square rounded text-xs font-medium transition-colors flex items-center justify-center",
                            picked
                              ? "bg-muted text-muted-foreground cursor-not-allowed opacity-40"
                              : selected
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted/50 hover:bg-muted text-foreground border border-border"
                          )}
                        >
                          {num}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="guess-value">Your Guess</Label>
                  <Input
                    id="guess-value"
                    placeholder={
                      selectedGame.game_type === "weight" ? "e.g., 2.5 kg" 
                      : selectedGame.game_type === "name" ? "e.g., Barnaby" 
                      : "e.g., 487"
                    }
                    value={guessValue}
                    onChange={e => setGuessValue(e.target.value)}
                  />
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPlayDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmitEntry} disabled={submitting}>
                  {submitting ? "Submitting..." : selectedGame.game_type === "number" && selectedNumbers.length > 0
                    ? `Submit ${selectedNumbers.length} Number(s)`
                    : "Submit Guess"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
